import { supabase } from "@/integrations/supabase/client";
import { logger } from "@/utils/logger";

export interface DraftData {
  feedback: string;
  submitter_name?: string;
  submitter_email?: string;
  submitter_role?: string;
  submitter_department?: string;
  startedAt: number;
  // Multi-step form fields
  step1?: string;
  step2?: string;
  step3?: string;
  currentStep?: number;
  isMultiStep?: boolean;
  // File attachments
  attachmentIds?: string[];
  [key: string]: string | number | boolean | string[] | undefined; // Index signature for JSON compatibility
}

// Generate a session ID for the user (stored in localStorage)
export const getSessionId = (): string => {
  let sessionId = localStorage.getItem('feedback_session_id');
  if (!sessionId) {
    sessionId = `session_${Date.now()}_${Math.random().toString(36).substring(7)}`;
    localStorage.setItem('feedback_session_id', sessionId);
  }
  return sessionId;
};

// Save draft to localStorage
export const saveDraftToLocalStorage = (pageId: string, data: DraftData): void => {
  const key = `feedback_draft_${pageId}`;
  localStorage.setItem(key, JSON.stringify(data));
  localStorage.setItem(`${key}_timestamp`, Date.now().toString());
};

// Load draft from localStorage
export const loadDraftFromLocalStorage = (pageId: string): DraftData | null => {
  const key = `feedback_draft_${pageId}`;
  const data = localStorage.getItem(key);
  if (!data) return null;
  
  try {
    return JSON.parse(data);
  } catch {
    return null;
  }
};

// Clear draft from localStorage
export const clearDraftFromLocalStorage = (pageId: string): void => {
  const key = `feedback_draft_${pageId}`;
  localStorage.removeItem(key);
  localStorage.removeItem(`${key}_timestamp`);
};

// Get last save timestamp
export const getLastSaveTimestamp = (pageId: string): number | null => {
  const key = `feedback_draft_${pageId}`;
  const timestamp = localStorage.getItem(`${key}_timestamp`);
  return timestamp ? parseInt(timestamp) : null;
};

// Save draft to database (cloud backup)
export const saveDraftToDatabase = async (pageId: string, data: DraftData): Promise<void> => {
  const sessionId = getSessionId();
  
  try {
    // Check if draft exists
    const { data: existing } = await supabase
      .from('feedback_drafts')
      .select('id')
      .eq('page_id', pageId)
      .eq('session_id', sessionId)
      .maybeSingle();

    if (existing) {
      // Update existing draft
      await supabase
        .from('feedback_drafts')
        .update({ 
          draft_content: data as any,
          expires_at: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString()
        })
        .eq('id', existing.id);
    } else {
      // Insert new draft
      await supabase
        .from('feedback_drafts')
        .insert({
          page_id: pageId,
          session_id: sessionId,
          draft_content: data as any,
        });
    }
  } catch (error) {
    logger.error('Error saving draft to database:', error);
    // Fail silently - localStorage is the primary backup
  }
};

// Load draft from database (cloud backup)
export const loadDraftFromDatabase = async (pageId: string): Promise<DraftData | null> => {
  const sessionId = getSessionId();
  
  try {
    const { data, error } = await supabase
      .from('feedback_drafts')
      .select('draft_content')
      .eq('page_id', pageId)
      .eq('session_id', sessionId)
      .maybeSingle();

    if (error || !data) return null;
    return data.draft_content as any as DraftData;
  } catch (error) {
    logger.error('Error loading draft from database:', error);
    return null;
  }
};

// Clear draft from database
export const clearDraftFromDatabase = async (pageId: string): Promise<void> => {
  const sessionId = getSessionId();
  
  try {
    await supabase
      .from('feedback_drafts')
      .delete()
      .eq('page_id', pageId)
      .eq('session_id', sessionId);
  } catch (error) {
    logger.error('Error clearing draft from database:', error);
  }
};
